import cn from 'classnames';
import Image from 'next/image';
import { IframeHTMLAttributes, useState } from 'react';

import { InstagramEmbed } from './InstagramEmbed';

export enum EVideoPlayerVariant {
  YOUTUBE = 'youtube',
  INSTAGRAM = 'instagram',
  CUSTOM = 'custom',
}

type TVideoPlayerProps = IframeHTMLAttributes<HTMLIFrameElement> & {
  src: string;
  title: string;
  vertical?: boolean;
  variant: EVideoPlayerVariant;
  fallbackImage?: string;
  controls?: boolean;
  autoPlay?: boolean;
};

export const VideoPlayer = ({
  src,
  title,
  className,
  vertical = false,
  variant,
  fallbackImage,
  controls = true,
  autoPlay = true,
  ...props
}: TVideoPlayerProps) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  if (variant === EVideoPlayerVariant.INSTAGRAM) {
    return (
      <div className={cn('relative w-full aspect-[9/16]', className)}>
        <InstagramEmbed url={src} fallbackImage={fallbackImage} />
      </div>
    );
  }

  return (
    <div className={cn('relative w-full', vertical ? 'aspect-[9/16]' : 'aspect-video', className)}>
      {!isLoaded && <div className="h-full bg-gray-200 animate-pulse rounded-xl" />}
      {variant === EVideoPlayerVariant.CUSTOM ? (
        <video
          className="absolute top-0 left-0 w-full h-full rounded-xl"
          controls={controls}
          onCanPlay={() => setIsLoaded(true)}
          preload="metadata"
          muted
          autoPlay={autoPlay}>
          <source src={src} type="video/mp4" />
          {fallbackImage && (
            <Image
              src={fallbackImage}
              alt="Video fallback"
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              placeholder="blur"
              blurDataURL={fallbackImage}
              priority={true}
              quality={75}
            />
          )}
        </video>
      ) : (
        <iframe
          className="absolute top-0 left-0 w-full h-full rounded-xl"
          src={src}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          {...props}
        />
      )}
    </div>
  );
};
